import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { patientService } from '../../patient/services/patient-service';
import { RootState } from '../../shared/state/root-reducer';
import { AppDispatch } from '../../shared/state/store';
import { setSelectedEncounter, setSelectedPatientV2 } from '../../patient/state/patient-slice';
import { addPatientToRecentPatientsIfNeeded } from '../../patient/state/patient-thunk';
import { showErrorStatus } from '../../security/state/user-slice';
import { getPatientsAndChampionsList } from '../hub-utils';
import { fetchUserPaymentMethods } from '../services/payment-method-service';
import { fetchProgram } from '../services/program-service';
import { PayorWithUserStatus, UserLookupState } from '../interfaces/payment-hub-interfaces';
import { PayorListEntry } from '../../shared/model/payor-list-entry';

export interface UsePaymentHubDataOptions {
  guarantorsOnly?: boolean;
  autoSelectFirstPayor?: boolean;
}

export interface UsePaymentHubDataResult {
  // URL params
  patientId: string | undefined;
  patientEncounterId: string | undefined;
  
  // Loading states
  isPatientLoading: boolean;
  isEncounterLoading: boolean;
  isPayorsLoading: boolean;
  isLoading: boolean;
  
  // Data
  selectedPatient: ReturnType<typeof useSelector<RootState, RootState['patientContext']['selectedPatient']>>;
  selectedEncounter: ReturnType<typeof useSelector<RootState, RootState['patientContext']['selectedEncounter']>>;
  authToken: string | undefined;
  payorsWithStatus: PayorWithUserStatus[];
  
  // Derived data
  patientName: string;
  paymentProgram: any;
  programId: number | undefined;
  pendingBalance: number;
  existingRecurringPaymentMethodId: number | null;
  
  // Payor selection
  selectedPayorId: string;
  setSelectedPayorId: (id: string) => void;
  selectedPayorUserLookupState: UserLookupState;
  selectedPayorBillingDetails: { firstName: string; lastName: string; email: string };
  setSelectedPayorBillingDetails: React.Dispatch<React.SetStateAction<{ firstName: string; lastName: string; email: string }>>;
  handlePayorChange: (payorId: string, payorsList?: PayorWithUserStatus[]) => Promise<void>;
  
  // Navigation
  navigate: ReturnType<typeof useNavigate>;
  dispatch: AppDispatch;
}

const initialUserLookupState: UserLookupState = {
  isChecking: false,
  isChecked: false,
  userExists: false,
  userId: null,
  stripeCustomerId: null,
  savedPaymentMethods: [],
};

export function usePaymentHubData(options: UsePaymentHubDataOptions = {}): UsePaymentHubDataResult {
  const { guarantorsOnly = false, autoSelectFirstPayor = false } = options;
  
  const { patientId, patientEncounterId } = useParams<{ patientId: string; patientEncounterId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const selectedPatient = useSelector((state: RootState) => state.patientContext.selectedPatient);
  const selectedEncounter = useSelector((state: RootState) => state.patientContext.selectedEncounter);
  const authToken = useSelector((state: RootState) => state?.userContext?.userProfile?.tokenState?.jwtIdToken);

  const [isPatientLoading, setIsPatientLoading] = useState(false);
  const [isEncounterLoading, setIsEncounterLoading] = useState(false);
  const [isPayorsLoading, setIsPayorsLoading] = useState(false);
  const [isProgramLoading, setIsProgramLoading] = useState(false);

  const [payorsWithStatus, setPayorsWithStatus] = useState<PayorWithUserStatus[]>([]);
  const [v3ProgramData, setV3ProgramData] = useState<any>(null);
  const [selectedPayorId, setSelectedPayorId] = useState<string>('');
  const [selectedPayorUserLookupState, setSelectedPayorUserLookupState] = useState<UserLookupState>(initialUserLookupState);
  const [selectedPayorBillingDetails, setSelectedPayorBillingDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });


  const hasPopulatedPayorsRef = useRef<string>('');
  const isProcessingRef = useRef<string>('');

  // Load patient data
  useEffect(() => {
    if (!patientId) return;

    (async () => {
      setIsPatientLoading(true);
      try {
        const res = await patientService.getPatient(parseInt(patientId));
        if (!res.hasErrors && res.entity) {
          dispatch(setSelectedPatientV2(res.entity));
          dispatch(addPatientToRecentPatientsIfNeeded(parseInt(patientId)));
        } else {
          dispatch(showErrorStatus('Patient not found.'));
          navigate('/hub');
        }
      } catch (error) {
        console.error('Error loading patient:', error);
        dispatch(showErrorStatus('Failed to load patient data'));
        navigate('/hub');
      } finally {
        setIsPatientLoading(false);
      }
    })();
  }, [patientId, dispatch, navigate]);

  // Load encounter data
  useEffect(() => {
    if (!patientId || !patientEncounterId) return;

    (async () => {
      setIsEncounterLoading(true);
      try {
        const res = await patientService.getPatientInstanceOfCare({
          patientId: parseInt(patientId),
          encounterId: parseInt(patientEncounterId),
        });
        if (!res.hasErrors && res.entity) {
          if (res.entity.patientId !== parseInt(patientId)) {
            dispatch(showErrorStatus('Encounter does not belong to this patient.'));
            navigate('/hub');
            return;
          }
          dispatch(setSelectedEncounter(res.entity));
        } else {
          dispatch(showErrorStatus('Encounter not found.'));
          navigate('/hub');
        }
      } catch (error) {
        console.error('Error loading encounter:', error);
        dispatch(showErrorStatus('Failed to load encounter data'));
        navigate('/hub');
      } finally {
        setIsEncounterLoading(false);
      }
    })();
  }, [patientId, patientEncounterId, dispatch, navigate]);

  // Fetch program from v3 API to get recurringUserPaymentMethodId
  useEffect(() => {
    const programId = selectedEncounter.patientPaymentProgram?.[0]?.patientPaymentProgramId;
    if (!programId || !authToken) return;

    (async () => {
      setIsProgramLoading(true);
      try {
        const result = await fetchProgram({ programId, authToken });
        if (result.success && result.program) {
          setV3ProgramData(result.program);
        }
      } catch (error) {
        console.error('Error fetching program from v3 API:', error);
      } finally {
        setIsProgramLoading(false);
      }
    })();
  }, [selectedEncounter.patientPaymentProgram, authToken]);

  // Handle payor change - updates billing details and fetches payment methods
  const handlePayorChange = useCallback(async (payorId: string, payorsList?: PayorWithUserStatus[]) => {
    setSelectedPayorId(payorId);

    const listToSearch = payorsList || payorsWithStatus;
    const selectedPayor = listToSearch.find(g => g.id?.toString() === payorId);
    if (!selectedPayor) return;

    // Parse first/last name from the name field
    const nameParts = selectedPayor.name?.split(' ') || [];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    setSelectedPayorBillingDetails({
      firstName,
      lastName,
      email: selectedPayor.email || '',
    });

    // Check if user has payment methods from the payor list
    if (selectedPayor.userId && selectedPayor.paymentMethods && selectedPayor.paymentMethods.length > 0) {
      setSelectedPayorUserLookupState({
        isChecking: false,
        isChecked: true,
        userExists: true,
        userId: selectedPayor.userId.toString(),
        stripeCustomerId: null,
        savedPaymentMethods: selectedPayor.paymentMethods,
      });
    } else if (selectedPayor.userId) {
      // Fetch payment methods if userId exists but paymentMethods not loaded
      setSelectedPayorUserLookupState({
        isChecking: true,
        isChecked: false,
        userExists: true,
        userId: selectedPayor.userId.toString(),
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });

      try {
        const result = await fetchUserPaymentMethods({ userId: selectedPayor.userId, authToken });
        setSelectedPayorUserLookupState(prev => ({
          ...prev,
          isChecking: false,
          isChecked: true,
          savedPaymentMethods: result.paymentMethods || [],
        }));
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        setSelectedPayorUserLookupState(prev => ({
          ...prev,
          isChecking: false,
          isChecked: true,
          savedPaymentMethods: [],
        }));
      }
    } else {
      setSelectedPayorUserLookupState({
        isChecking: false,
        isChecked: true,
        userExists: false,
        userId: null,
        stripeCustomerId: null,
        savedPaymentMethods: [],
      });
    }
  }, [payorsWithStatus, authToken]);

  // Load payors when patient and encounter are ready
  useEffect(() => {
    if (!selectedPatient?.patientId || selectedPatient.patientId <= 0 || !selectedPatient?.contact || !selectedEncounter?.patientEncounterId || !authToken) {
      return;
    }

    const currentKey = `${selectedPatient.patientId}-${selectedEncounter.patientEncounterId}`;
    
    if (hasPopulatedPayorsRef.current === currentKey) {
      return;
    }

    if (isProcessingRef.current === currentKey) {
      return;
    }

    const loadPayors = async () => {
      isProcessingRef.current = currentKey;
      setIsPayorsLoading(true);

      try {
        const payorsList = await getPatientsAndChampionsList(selectedPatient, selectedEncounter, false, authToken);
        
        // Filter to guarantors only if specified
        let filteredPayors = guarantorsOnly 
          ? payorsList.filter((payor: PayorListEntry) => payor.isGuarantor)
          : payorsList;

        // Map to PayorWithUserStatus format
        const payorsWithUserStatus: PayorWithUserStatus[] = filteredPayors.map((payor: PayorListEntry) => ({
          ...payor,
          type: payor.type || 'champion',
        }));

        setPayorsWithStatus(payorsWithUserStatus);
        hasPopulatedPayorsRef.current = currentKey;

        // Auto-select first payor if specified
        if (autoSelectFirstPayor && payorsWithUserStatus.length > 0) {
          const firstPayor = payorsWithUserStatus[0];
          await handlePayorChange(firstPayor.id?.toString() || '', payorsWithUserStatus);
        }
      } catch (error) {
        console.error('Error loading payors:', error);
        setPayorsWithStatus([]);
      } finally {
        setIsPayorsLoading(false);
        isProcessingRef.current = '';
      }
    };

    loadPayors();
  }, [selectedPatient, selectedEncounter, authToken, guarantorsOnly, autoSelectFirstPayor, handlePayorChange]);

  // Derived data
  const patientName = `${selectedPatient.contact?.firstName || ''} ${selectedPatient.contact?.lastName || ''}`.trim() || 'Loading...';
  const paymentProgram = selectedEncounter.patientPaymentProgram?.[0];
  const programId = paymentProgram?.patientPaymentProgramId;
  const pendingBalance = paymentProgram?.patientPaymentSchedule?.pfrPendingBalance || selectedEncounter.pfrAmt;
  
  // Get recurringUserPaymentMethodId from v3 program data (more reliable than v2 encounter data)
  const existingRecurringPaymentMethodId = v3ProgramData?.patientPaymentSchedule?.recurringUserPaymentMethodId 
    ?? (paymentProgram?.patientPaymentSchedule as any)?.recurringUserPaymentMethodId 
    ?? null;

  const isLoading = isPatientLoading || isEncounterLoading || isPayorsLoading || isProgramLoading;

  return {
    // URL params
    patientId,
    patientEncounterId,
    
    // Loading states
    isPatientLoading,
    isEncounterLoading,
    isPayorsLoading,
    isLoading,
    
    // Data
    selectedPatient,
    selectedEncounter,
    authToken,
    payorsWithStatus,
    
    // Derived data
    patientName,
    paymentProgram,
    programId,
    pendingBalance,
    existingRecurringPaymentMethodId,
    
    // Payor selection
    selectedPayorId,
    setSelectedPayorId,
    selectedPayorUserLookupState,
    selectedPayorBillingDetails,
    setSelectedPayorBillingDetails,
    handlePayorChange,
    
    // Navigation
    navigate,
    dispatch,
  };
}
